import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

import * as React from "react";
import draftToHtml from "draftjs-to-html";
import {
  ContentState,
  EditorState,
  convertToRaw,
  convertFromHTML
} from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import styled, { css } from "styled-components";

interface WysiwygEditorProps {
  initalValue?: string;
  handleChange: (value: string) => void;
  frame?: boolean;
  value?: EditorState;
  toolbarCustomButtons?: Array<React.ReactElement<HTMLElement>>;
}

export interface WysiwygEditorRef {
 clearState: () => void;
}

const WysiwygEditor: React.ForwardRefRenderFunction<WysiwygEditorRef, WysiwygEditorProps> = (props, ref) => {
  const [ editorState, setEditorState ] = React.useState(EditorState.createEmpty());
  const [ hasSetInitialValue, setHasSetInitialValue ] = React.useState(false);

  React.useEffect(() => {
    if (props.initalValue && props.initalValue.length > 0 && !hasSetInitialValue) {
      const contentState = ContentState.createFromBlockArray(convertFromHTML(props.initalValue).contentBlocks);

      if (contentState.getBlockMap().first()) {
        setHasSetInitialValue(true);
        setEditorState(EditorState.createWithContent(contentState));
      }
    }
  }, [ props.initalValue, hasSetInitialValue ]);
  React.useImperativeHandle<WysiwygEditorRef, WysiwygEditorRef>(ref, () => ({ clearState: () => setEditorState(EditorState.createEmpty()) }));

  return (
    <Wrapper className={props.frame ? "frame" : "noframe"}>
      <Editor
        wrapperClassName="wrapper-class"
        editorClassName="editor-class"
        toolbarClassName="toolbar-class"
        editorState={props.value || editorState}
        toolbar={{
          options: [
            "inline",
            "blockType",
            "list",
            "link"
          ],
          inline: {
            options: [
              "bold",
              "italic",
              "underline"
            ]
          },
          blockType: {
            options: [
              "Normal",
              "H1",
              "H2",
              "H3",
              "H4",
              "H5",
              "H6"
            ]
          },
          list: { options: [ "unordered" ] }
        }}
        onEditorStateChange={editorState => {
          const content = draftToHtml(convertToRaw(editorState.getCurrentContent()));

          setEditorState(editorState);
          props.handleChange(content);
        }}
        toolbarCustomButtons={props.toolbarCustomButtons}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  ${({ theme }) => css`
    font-size: ${theme.fonts.sizes.p};

    &.frame {
      background: white;
      border-radius: 4px;
      border: 2px solid ${theme.colors.lightGrey};
      &:hover {
        border-color: ${theme.colors.midGrey};
      }
      transition: all ease-in-out 0.2s;
      .toolbar-class {
        background: white;
      }
      .editor-class {
        border: none;
        border-top: 1px solid ${theme.colors.lightestGrey};
        border-radius: 0px;
      }
    }

    .toolbar-class {
      background: ${theme.colors.lightestGrey};
      border: 0;
      border-radius: 4px;

      .rdw-option-wrapper {
        border: 0;
        height: 30px;
        width: 30px;
        padding-top: 10px;
        background: none;
      }

      .rdw-block-dropdown span {
        padding: 5px;
      }

      .rdw-block-dropdown div {
        margin: 3px 0;
      }

      .rdw-dropdown-wrapper.rdw-block-dropdown {
        border: 0;
        margin: 0 10px;
        background: none;
      }
    }
    .editor-class {
      padding: 10px 20px;
      min-height: 130px;
      max-height: 400px;
      border: 1px solid ${theme.colors.lightestGrey};
      background: white;
      border-radius: 4px;

      ul {
        list-style: inherit;
        padding-top: ${theme.spacing.padding.sm};
        margin-left: 13px;
        li {
          letter-spacing: 0;
          font-weight: 400;
          line-height: 1.4em;
          font-size: ${theme.fonts.sizes.p};
          color: ${theme.fonts.colors.primary};
        }
      }
      p {
        padding-top: ${theme.spacing.padding.sm};
        letter-spacing: 0;
        font-weight: 400;
        line-height: 1.4em;
        font-size: ${theme.fonts.sizes.p};
        color: ${theme.fonts.colors.primary};

        b,
        strong {
          font-weight: bold;
        }
        em {
          font-style: italic;
        }
        u,
        ins {
          text-decoration: underline;
        }
      }
      h1 {
        font-size: ${theme.fonts.sizes.h1};
      }
      h2 {
        font-size: ${theme.fonts.sizes.h2};
      }
      h3 {
        font-size: ${theme.fonts.sizes.h3};
      }
      h4 {
        font-size: ${theme.fonts.sizes.h4};
      }
      h5 {
        font-size: ${theme.fonts.sizes.h5};
      }
      h6 {
        font-size: ${theme.fonts.sizes.h6};
      }
    }
  `}
`;

export default React.forwardRef(WysiwygEditor);
